import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from "react";
import axios from "axios";
import { useUser } from "../auth";
import { AccountSettings } from "@aika/types";

type DefaultSettings = {
  theme: string;
};

const defaultSettings: DefaultSettings = {
  theme: "aika"
};

export const SettingsContext = createContext<
  [AccountSettings, (s: React.SetStateAction<AccountSettings>) => void]
>(null!);

export const SettingsProvider = ({
  children
}: {
  children?: React.ReactNode;
}) => {
  const { user } = useUser();
  const [settings, _setSettings] = useState<AccountSettings>({
    ...defaultSettings
  } as AccountSettings);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user == null) {
      _setSettings({ ...defaultSettings } as AccountSettings);
      setLoading(false);
    } else {
      axios
        .get("/api/settings")
        .then(({ data }) => {
          _setSettings(data);
        })
        .finally(() => setLoading(false));
    }
  }, [user]);

  useEffect(() => {
    if (user != null && dirty) {
      axios.put("/api/settings", settings);
      setDirty(false);
    }
  }, [settings, dirty, user]);

  const setSettings = useCallback(
    (s: React.SetStateAction<AccountSettings>) => {
      setDirty(true);
      _setSettings(s);
    },
    [setDirty, _setSettings]
  );

  return (
    <SettingsContext.Provider value={[settings, setSettings]}>
      {!loading && children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
